<template>
    <Teleport to="body">
        <Transition :name="noAnimation ? '' : 'modal-fade'">
            <div v-if="show" class="modal-overlay" @click="handleBackdropClick">
                <div class="modal-container">
                    <!-- Close Button -->
                    <button v-if="!hideCloseButton" style="box-shadow:unset" class="modal-close-btn" @click="closeModal" title="סגור (ESC)">
                        <i class="material-icons">close</i>
                    </button>

                    <!-- Modal Content -->
                    <div class="modal-content">
                        <slot></slot>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { onUnmounted, watch, toRef } from 'vue'
import { useModalBackButton } from '@/composables/useModalBackButton'

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
    noAnimation: {
        type: Boolean,
        default: false
    },
    hideCloseButton: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['close'])

const closeModal = () => {
    emit('close')
}

// Allow closing with the browser / mobile (Android & iOS) back button
useModalBackButton(toRef(props, 'show'), closeModal)

const handleBackdropClick = (event) => {
    // Only close if clicking the backdrop itself, not the content
    if (event.target.classList.contains('modal-overlay')) {
        // Optionally enable backdrop click to close
        // closeModal()
    }
}

const handleEscapeKey = (event) => {
    if (event.key === 'Escape' && props.show) {
        closeModal()
    }
}

// Add/remove escape key listener when modal visibility changes
watch(() => props.show, (newValue) => {
    if (newValue) {
        document.addEventListener('keydown', handleEscapeKey)
        // Prevent body scrolling when modal is open
        document.body.style.overflow = 'hidden'
    } else {
        document.removeEventListener('keydown', handleEscapeKey)
        // Restore body scrolling when modal is closed
        document.body.style.overflow = ''
    }
})

onUnmounted(() => {
    document.removeEventListener('keydown', handleEscapeKey)
    document.body.style.overflow = ''
})
</script>

<style scoped>
/* Modal Overlay - Full Screen */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    padding: 20px;
}

/* Modal Container - Centered Dialog */
.modal-container {
    position: relative;
    width: 100%;
    max-width: 500px;
    height: 95%;
    background-color: #f5f5f5;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

/* Close Button */
.modal-close-btn {
    position: absolute;
    top: 2px;
    left: 2px;
    width: auto;
    height: auto;
    background: none;
    border: none;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 0.2s ease;
    z-index: 10000;
    padding: 5px;
}

.modal-close-btn:hover {
    transform: scale(1.05);
}

.modal-close-btn i {
    font-size: 32px;
    color: var(--dark-red);
    transition: all 0.2s ease;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.modal-close-btn:hover i {
    color: #c62828;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

/* Modal Content - Scrollable */
.modal-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    height: 100%;
    -webkit-overflow-scrolling: touch;
}

/* Custom Scrollbar for Modal Content */
.modal-content::-webkit-scrollbar {
    width: 10px;
}

.modal-content::-webkit-scrollbar-track {
    background: #e0e0e0;
}

.modal-content::-webkit-scrollbar-thumb {
    background: var(--dark-blue);
    border-radius: 5px;
}

.modal-content::-webkit-scrollbar-thumb:hover {
    background: var(--light-blue);
}

/* Modal Fade Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}

.modal-fade-enter-active .modal-container,
.modal-fade-leave-active .modal-container {
    transition: transform 0.3s ease;
}

.modal-fade-enter-from .modal-container {
    transform: scale(0.95);
}

.modal-fade-leave-to .modal-container {
    transform: scale(0.95);
}

/* Responsive Design - full screen on mobile */
@media (max-width: 768px) {
    .modal-overlay {
        padding: 0;
    }

    .modal-container {
        width: 100%;
        max-width: 100%;
        height: 100%;
        border-radius: 0;
    }

    .modal-close-btn {
        top: 2px;
        left: 2px;
    }

    .modal-close-btn i {
        font-size: 28px;
    }

    /* Hide the scrollbar on mobile but keep scrolling working */
    .modal-content {
        scrollbar-width: none;      /* Firefox */
        -ms-overflow-style: none;   /* IE / old Edge */
    }

    .modal-content::-webkit-scrollbar {
        display: none;              /* Chrome, Safari, Samsung Internet, Android WebView */
        width: 0;
        height: 0;
    }
}
</style>
